// src/services/AttorneyService.ts
import {
  collection,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  addDoc,
  query,
  where,
  Timestamp,
  orderBy,
  writeBatch,
} from "firebase/firestore";
import { Attorney, CaseAttorney } from "@visitproof/types";
import { db } from "../firebase";
import { clean } from "../utils/utils";

const attorneyCollectionRef = collection(db, "attorneys");
const caseAttorneyCollectionRef = collection(db, "caseAttorneys");

const AttorneyService = {
  // ============================================================================
  // ATTORNEY CRUD
  // ============================================================================

  async createAttorney(
    attorneyData: Omit<Attorney, "id" | "createdAt" | "updatedAt">,
  ): Promise<string> {
    const newAttorney: Omit<Attorney, "id"> = {
      ...attorneyData,
      status: attorneyData.status || "active",
      activeCaseCount: 0,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };

    const attorneyRef = await addDoc(attorneyCollectionRef, clean(newAttorney));
    return attorneyRef.id;
  },

  async getAttorney(attorneyId: string): Promise<Attorney | null> {
    const attorneyDoc = await getDoc(doc(attorneyCollectionRef, attorneyId));
    if (attorneyDoc.exists()) {
      return { id: attorneyDoc.id, ...attorneyDoc.data() } as Attorney;
    }
    return null;
  },

  async getAllAttorneys(accountId: string): Promise<Attorney[]> {
    const attorneySnapshot = await getDocs(
      query(
        attorneyCollectionRef,
        where("accountId", "==", accountId),
        orderBy("lastName", "asc"),
      ),
    );
    return attorneySnapshot.docs.map(
      (doc) => ({ id: doc.id, ...doc.data() }) as Attorney,
    );
  },

  async getActiveAttorneys(accountId: string): Promise<Attorney[]> {
    const attorneySnapshot = await getDocs(
      query(
        attorneyCollectionRef,
        where("accountId", "==", accountId),
        where("status", "==", "active"),
        orderBy("lastName", "asc"),
      ),
    );
    return attorneySnapshot.docs.map(
      (doc) => ({ id: doc.id, ...doc.data() }) as Attorney,
    );
  },

  async updateAttorney(
    attorneyId: string,
    updatedData: Partial<Omit<Attorney, "id" | "createdAt">>,
  ): Promise<void> {
    const attorneyDocRef = doc(attorneyCollectionRef, attorneyId);
    await updateDoc(
      attorneyDocRef,
      clean({
        ...updatedData,
        updatedAt: Timestamp.now(),
      }),
    );
  },

  async deleteAttorney(attorneyId: string): Promise<void> {
    // First, remove all case assignments for this attorney
    const assignments = await getDocs(
      query(caseAttorneyCollectionRef, where("attorneyId", "==", attorneyId)),
    );

    const batch = writeBatch(db);

    assignments.docs.forEach((assignmentDoc) => {
      batch.delete(assignmentDoc.ref);
    });

    // Then delete the attorney
    batch.delete(doc(attorneyCollectionRef, attorneyId));

    await batch.commit();
  },

  // ============================================================================
  // CASE-ATTORNEY ASSIGNMENTS
  // ============================================================================

  async assignAttorneyToCase(
    assignmentData: Omit<CaseAttorney, "id" | "createdAt" | "updatedAt">,
  ): Promise<string> {
    const newAssignment: Omit<CaseAttorney, "id"> = {
      ...assignmentData,
      isActive: true,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };

    const assignmentRef = await addDoc(
      caseAttorneyCollectionRef,
      clean(newAssignment),
    );

    // Update the attorney's active case count
    await this.updateAttorneyCaseCount(assignmentData.attorneyId);

    return assignmentRef.id;
  },

  async getCaseAttorneys(caseId: string): Promise<CaseAttorney[]> {
    const snapshot = await getDocs(
      query(
        caseAttorneyCollectionRef,
        where("caseId", "==", caseId),
        where("isActive", "==", true),
      ),
    );
    return snapshot.docs.map(
      (doc) => ({ id: doc.id, ...doc.data() }) as CaseAttorney,
    );
  },

  async getAttorneyCases(
    accountId: string,
    attorneyId: string,
  ): Promise<CaseAttorney[]> {
    const snapshot = await getDocs(
      query(
        caseAttorneyCollectionRef,
        where("accountId", "==", accountId),
        where("attorneyId", "==", attorneyId),
      ),
    );
    return snapshot.docs.map(
      (doc) => ({ id: doc.id, ...doc.data() }) as CaseAttorney,
    );
  },

  async updateCaseAttorney(
    assignmentId: string,
    updatedData: Partial<Omit<CaseAttorney, "id" | "createdAt">>,
  ): Promise<void> {
    const assignmentDocRef = doc(caseAttorneyCollectionRef, assignmentId);
    await updateDoc(
      assignmentDocRef,
      clean({
        ...updatedData,
        updatedAt: Timestamp.now(),
      }),
    );
  },

  async removeAttorneyFromCase(assignmentId: string): Promise<void> {
    // Get the assignment first to know which attorney to update
    const assignmentDoc = await getDoc(
      doc(caseAttorneyCollectionRef, assignmentId),
    );

    if (!assignmentDoc.exists()) {
      return;
    }

    const assignment = assignmentDoc.data() as CaseAttorney;

    // Soft delete by setting isActive to false
    await updateDoc(doc(caseAttorneyCollectionRef, assignmentId), {
      isActive: false,
      endDate: Timestamp.now(),
      updatedAt: Timestamp.now(),
    });

    // Update the attorney's active case count
    await this.updateAttorneyCaseCount(assignment.attorneyId);
  },

  async updateAttorneyCaseCount(attorneyId: string): Promise<void> {
    const activeCases = await getDocs(
      query(
        caseAttorneyCollectionRef,
        where("attorneyId", "==", attorneyId),
        where("isActive", "==", true),
      ),
    );

    await updateDoc(doc(attorneyCollectionRef, attorneyId), {
      activeCaseCount: activeCases.size,
      updatedAt: Timestamp.now(),
    });
  },

  // ============================================================================
  // SEARCH & UTILITIES
  // ============================================================================

  async searchAttorneys(
    accountId: string,
    searchTerm: string,
  ): Promise<Attorney[]> {
    // Get all attorneys and filter client-side (Firestore doesn't support full-text search)
    const allAttorneys = await this.getAllAttorneys(accountId);
    const searchLower = searchTerm.toLowerCase();

    return allAttorneys.filter((attorney) => {
      const fullName =
        `${attorney.firstName} ${attorney.lastName}`.toLowerCase();
      const firm = attorney.firmName?.toLowerCase() || "";
      const email = attorney.email?.toLowerCase() || "";

      return (
        fullName.includes(searchLower) ||
        firm.includes(searchLower) ||
        email.includes(searchLower)
      );
    });
  },
};

export default AttorneyService;
